<script>
    import {onMount, tick} from 'svelte';
    import {forms} from "./lib/forms.js";
    import FormModal from './Forms/FormModal.svelte';
    import {confirm} from './Components/Confirmation.svelte';
    import { notifier } from '@beyonk/svelte-notifications';

    import sortable, {getBeforeAfter} from "./lib/sortable";

    import {db} from './Database.svelte';

    import Loader from './Components/Loader.svelte';
    import ControlEditor from './Components/ControlEditor.svelte';
    import LibraryControlEditor from './Components/AssessmentType/LibraryControlEditor.svelte';
    import AssessmentTypeNav from './Components/AssessmentTypeNav.svelte';

    import {showContextMenu} from './Components/ContextMenu.svelte';

    import {addModal} from './Components/ModalManager.svelte';
    import AddCategoryModal from './Components/AssessmentType/AddCategoryModal.svelte';
    import AddSubcategoryModal from './Components/AssessmentType/AddSubcategoryModal.svelte';

    import {push, replace} from 'svelte-spa-router'

    import AssessmentTypeModel, {getSortedValues} from './Models/assessmentTypeModel'
    import QuestionLibrary from './Models/QuestionLibraryModel.js';
    import { addFormModal } from './Forms/ModalFormManager.svelte';

    export let params;

    let assessment_type_model;
    let assessment_type;
    let ctrlKey = false;

    let collapsed = {};

    let formData;

    onMount(() => {
        assessment_type_model = new AssessmentTypeModel(params.id);

        let unsub = assessment_type_model.store.subscribe((data) => {
            if (!data) return;

            assessment_type = data;
        })

        return () => {
            unsub();
        }
    })

    function showForm(form) {
        return new Promise((resolve, reject) => {
            form.cancelAction = () => {
                formData = undefined;
                reject()
            };

            form.saveAction = (data) => {
                formData = undefined;
                resolve(data)
            };

            formData = form;
        })
    }

    function addCategory() {
        addModal(AddCategoryModal).then(async result => {
            if (result.control_type == "Library") {
                let ts = +(new Date())
                let promises = [];
                for (let category of result.selected_library.categories) {
                    if (result.selected_categories.includes(category.id) ) {
                        promises.push(assessment_type_model.addLibraryCategory(category.id, ts++));
                    }
                }

                await Promise.all(promises);

                if (result.selected_categories.length > 1){
                    notifier.success("Categories Added");
                } else {
                    notifier.success("Category Added");
                }
                return;
            } else {
                await assessment_type_model.addCategory(result.data);
                notifier.success("Category Added");
            }

        }).catch((err) => {
            if (err != "cancel") throw(err)
        }) // ignore cancel
    }

    function deleteCategory(category) {
        confirm(`Are you sure you wish to delete category "${category.details.name}"`).then(() => {
            if (params.category_id == category.details.category_id) {
                replace(`/assessmentTypes/${params.id}/`);
            }
            assessment_type_model.deleteCategory(category).then(() => {
                notifier.success("Category Deleted")
            }).catch(err => {
                notifier.danger(err);
            });
        }).catch(() => null)
    }

    function addSubcategory(category) {
        showForm({
            title: "Add Subcategory",
            fields: forms['subcategory']
        }).then((data) => {
            assessment_type_model.addSubcategory(category.details.category_id, data);
            notifier.success("Category Added");
        })
    }

    function deleteSubcategory(subcategory) {
        confirm(`Are you sure you wish to delete the subcategory "${subcategory.details.name}"?`).then(() => {
            assessment_type_model.deleteSubcategory(subcategory).then(() => {
                notifier.success("Subcategory Deleted");
            }).catch(err => {
                notifier.danger(err);
            });
        }).catch(() => null);
    }

    function addControl(category, subcategory) {
        showForm({
            title: "Add Control",
            fields: forms['control']
        }).then((control) => {
            assessment_type_model.addControl(category.details.category_id, subcategory.details.subcategory_id, control).then(async (control) => {
                push(`/assessmentTypes/${params.id}/category/${category.details.category_id}/subcategory/${subcategory.details.subcategory_id}/control/${control.control_id}/`)
            });
        }).catch()
    }

    function deleteControl(control) {
        confirm(`Are you sure you wish to delete the control "${control.name}"?`).then(() => {
            assessment_type_model.deleteControl(control).then(() => {
                notifier.success("Control Deleted")
            });
            replace(`/assessmentTypes/${assessment_type.id}/`)
        }).catch(() => null);
    }

    function saveAssessmentType() {
        assessment_type_model.saveAssessmentType().then(() => {
            notifier.success("Assessment Type Updated");
        });
    }

    // Click Handlers
    function handleCategoryButton(evt, category) {
        if (ctrlKey) {
            deleteCategory(category);
        } else {
            addSubcategory(category);
        }
    }

    function handleSubcategoryButton(category, subcategory) {
        if (ctrlKey) {
            deleteSubcategory(subcategory);
        } else {
            addControl(category, subcategory);
        }
    }

    function editCategory(category) {
        showForm({
            title: "Edit Category",
            fields: forms['category'],
            data: category.details
        }).then(category => {
            assessment_type_model.saveCategory(category).then(() => {
                notifier.success("Category Saved");
            }).catch(err => {
                notifier.danger(err)
            })
        }).catch(() => null)
    }


    function editSubcategory(subcategory) {
        showForm({
            title: "Edit Category",
            fields: forms['subcategory'],
            data: subcategory.details
        }).then(subcategory => {
            assessment_type_model.saveSubcategory(subcategory).then(() => {
                notifier.success("Subcategory Saved");
            }).catch(err => {
                notifier.danger(err)
            })
        }).catch(() => null)
    }

    function handleCategoryMenu(evt, category) {
        showContextMenu(evt.clientX, evt.clientY, [
            {
                name: "Edit Category",
                action: () => editCategory(category)
            },
            {
                name: "Delete Category",
                action: () => deleteCategory(category)
            }
        ])
    }

    function handleSubcategoryMenu(evt, subcategory) {
        showContextMenu(evt.clientX, evt.clientY, [
            {
                name: "Edit Subcategory",
                action: () => editSubcategory(subcategory)
            },
            {
                name: "Delete Subcategory",
                action: () => deleteSubcategory(subcategory)
            }
        ])
    }

    function handleControlMenu(evt, control) {
        showContextMenu(evt.clientX, evt.clientY, [
            {
                name: "Delete Control",
                action: () => deleteControl(control)
            }
        ])
    }

    function handleLibrarySubcategoryMenu(evt, category, subcategory) {
        showContextMenu(evt.clientX, evt.clientY, [
            {
                name: "Exclude All",
                action: () => excludeSubcategoryControls(category, subcategory)
            },
            {
                name: "Include All",
                action: () => includeSubcategoryControls(category, subcategory)
            },
        ])
    }

    function handleLibraryControlMenu(evt, category, control) {
        let actions = [];
        if (category.details.include_controls && category.details.include_controls.includes(control.details.child_id)) {
            actions = [
                {
                    name: "Exclude Control",
                    action: () => excludeControl(category.details, control)
                }
            ]
        } else {
            actions = [
                {
                    name: "Include Control",
                    action: () => includeControl(category.details, control)
                },
            ]
        }
        showContextMenu(evt.clientX, evt.clientY, actions)
    }

    function excludeSubcategoryControls(category, subcategory) {
        const subcategory_controls = [...subcategory.controls].map(c => c.details.child_id);
        category.details.include_controls = category.details.include_controls.filter((c) => !subcategory_controls.includes(c));

        db.put(category.details);
    }

    function includeSubcategoryControls(category, subcategory) {
        const subcategory_controls = [...subcategory.controls].map(c => c.details.child_id);

        // use Set to remove duplicates
        const include_controls = new Set([...category.details.include_controls, ...subcategory_controls])
        category.details.include_controls = Array.from(include_controls);

        db.put(category.details);
    }

    async function excludeControl(category, control) {
        category.include_controls = category.include_controls.filter(c => c != control.details.child_id);

        await db.put(category);

        notifier.success("Control Excluded")
    }

    async function includeControl(category, control) {
        category.include_controls = [...category.include_controls, control.details.child_id];

        await db.put(category);

        notifier.success("Control Included")
    }

    async function moveCategory(evt) {
        if (evt.newIndex == evt.oldIndex) return;

        let categories = getSortedValues(assessment_type.categories);

        let {before, after} = getBeforeAfter(categories, evt.oldIndex, evt.newIndex);

        assessment_type_model.moveCategory(categories[evt.oldIndex], after, before);
    }

    function moveSubcategory(category, evt) {
        if (evt.oldIndex == evt.newIndex) return;

        let subcategories = getSortedValues(category.subcategories);

        let {before, after} = getBeforeAfter(subcategories, evt.oldIndex, evt.newIndex);

        assessment_type_model.moveSubcategory(category, subcategories[evt.oldIndex], after, before);
    }

    function moveControl(subcategory, evt) {
        if (evt.oldIndex == evt.newIndex) return;

        let controls = getSortedValues(subcategory.controls);

        let {before, after} = getBeforeAfter(controls, evt.oldIndex, evt.newIndex);

        assessment_type_model.moveControl(subcategory, controls[evt.oldIndex], after, before);
    }


    const memoizer = {};
    async function getCategoryLibrary(library_id) {
        if (memoizer[library_id]) return memoizer[library_id];

        let category_library = await QuestionLibrary.getStatic(library_id);

        memoizer[library_id] = category_library;

        return category_library;
    }
</script>

<Loader loading={!assessment_type}>
    <div class="container viewport is-fluid">
        <div class="columns viewport">
            <div class="column is-3">
                <AssessmentTypeNav assessment_type_id={params.id} />
                <h1 class="title is-1">
                    {assessment_type.details.name}
                    <button
                        class="button is-pulled-right"
                        on:click={addCategory}
                    >
                        Add Category
                    </button>
                </h1>

                <aside class="menu">
                    <ul class="menu-list" use:sortable on:sortEnd={(evt) => moveCategory(evt.detail)}>
                    {#each getSortedValues(assessment_type.categories) as category (category.details._id)}
                        {#if category.details.library_id}
                            <li>
                                {#await getCategoryLibrary(category.details.library_id)}
                                    Loading...
                                {:then category_library}
                                    <input type="checkbox" class="collapse" id={category.details._id} bind:checked={collapsed[category.details._id]}>
                                    <label for={category.details._id} on:contextmenu|preventDefault={evt => handleCategoryMenu(evt, category)}>
                                        <strong>{category_library.details.control_number}</strong>
                                        {category_library.details.name}
                                        <span class="is-pulled-right">⚭</span>
                                    </label>
                                    <ul class="collapsible">
                                        {#each [...category_library.subcategories] as subcategory (subcategory.id)}
                                            <li>
                                                <input type="checkbox" class="collapse" id={subcategory.details._id} bind:checked={collapsed[subcategory.details._id]}>
                                                <label for={subcategory.details._id} on:contextmenu|preventDefault={evt => handleLibrarySubcategoryMenu(evt, category, subcategory)}>
                                                    <strong>{subcategory.details.control_number}</strong>
                                                    {subcategory.details.name}
                                                    <span class="is-pulled-right">⚭</span>
                                                </label>
                                                <ul class="collapsible">
                                                    {#each [...subcategory.controls] as control (control.id)}
                                                        <li>
                                                            <a
                                                                class="link menu-item"
                                                                class:is-active={params.control_id == control.details.child_id}
                                                                href="#/assessmentTypes/{params.id}/libraryCategory/{category.details._id}/{subcategory.details._id}/{control.details.child_id}/"
                                                                on:contextmenu|preventDefault={(evt) => handleLibraryControlMenu(evt, category, control)}
                                                                >
                                                                <strong>{control.details.control_number}</strong>
                                                                {control.details.name}
                                                                {#if category.details.include_controls && category.details.include_controls.includes(control.details.child_id)}
                                                                    <span class="is-pulled-right">⚭</span>
                                                                {:else}
                                                                    <span class="is-pulled-right has-text-danger">⊘</span>
                                                                {/if}
                                                            </a>
                                                        </li>
                                                    {/each}
                                                </ul>
                                            </li>
                                        {/each}
                                    </ul>
                                {:catch e}
                                    <span on:contextmenu|preventDefault={evt => handleCategoryMenu(evt, category)}>
                                        Library Category Missing... ({e})
                                    </span>
                                {/await}
                            </li>
                        {:else}
                        <li>
                            <input type="checkbox" class="collapse" id={category.details._id} bind:checked={collapsed[category.details._id]}>
                            <label for={category.details._id} on:contextmenu|preventDefault={evt => handleCategoryMenu(evt, category)}>
                                <strong>{category.details.control_number}</strong>
                                {category.details.name}
                                <span
                                    class="button is-small is-pulled-right"
                                    on:click|preventDefault={(evt) => handleCategoryButton(evt, category)}
                                    class:is-danger={ctrlKey}
                                >
                                    {#if ctrlKey}
                                        Delete Category
                                    {:else}
                                        Add Subcategory
                                    {/if}
                                </span>
                            </label>
                            <ul class="collapsible" use:sortable on:sortEnd={(evt) => moveSubcategory(category, evt.detail)}>
                                {#each getSortedValues(category.subcategories) as subcategory (subcategory.details._id)}
                                        <li>
                                            <input type="checkbox" class="collapse" id={subcategory.details._id} bind:checked={collapsed[subcategory.details._id]}>
                                            <label for={subcategory.details._id} on:contextmenu|preventDefault={evt => handleSubcategoryMenu(evt, subcategory)}>
                                                <strong>{subcategory.details.control_number}</strong>
                                                {subcategory.details.name}
                                                <span
                                                    class="button is-small is-pulled-right"
                                                    on:click|preventDefault={() => handleSubcategoryButton(category, subcategory)}
                                                    class:is-danger={ctrlKey}
                                                >
                                                    {#if ctrlKey}
                                                        Delete Subcategory
                                                    {:else}
                                                        Add Control
                                                    {/if}
                                                </span>
                                            </label>
                                            <ul class="collapsible" use:sortable on:sortEnd={(evt) => moveControl(subcategory, evt.detail)}>
                                                {#each getSortedValues(subcategory.controls) as control (control._id)}
                                                    {#if control.library_id}
                                                        <li>
                                                            <a class="link menu-item"
                                                                href="#/assessmentTypes/{params.id}/category/{category.details.category_id}/subcategory/{subcategory.details.subcategory_id}/control/{control.control_id}/"
                                                                class:is-active={params.control_id == control.control_id}
                                                                on:contextmenu|preventDefault={evt => handleControlMenu(evt, control)}
                                                            >
                                                                {#await db.get(control.library_id)}
                                                                    Loading...
                                                                {:then ctrl}
                                                                    <strong>{ctrl.control_number}</strong>
                                                                    {ctrl.name}
                                                                    <span class="is-pulled-right">⚭</span>
                                                                {/await}
                                                            </a>
                                                        </li>
                                                    {:else}
                                                        <li>
                                                            <span
                                                                class="button is-danger delete-control is-pulled-right is-small"
                                                                class:hidden={!ctrlKey}
                                                                on:click|stopPropagation={() => deleteControl(control)}
                                                            >
                                                                Delete Control
                                                            </span>
                                                            <a
                                                                class="link menu-item"
                                                                href="#/assessmentTypes/{params.id}/category/{category.details.category_id}/subcategory/{subcategory.details.subcategory_id}/control/{control.control_id}/"
                                                                class:is-active={params.control_id == control.control_id}
                                                                on:contextmenu|preventDefault={evt => handleControlMenu(evt, control)}

                                                            >
                                                                <strong>{control.control_number}</strong>
                                                                {control.name}
                                                            </a>
                                                        </li>
                                                    {/if}
                                                {/each}
                                            </ul>
                                        </li>
                                {/each}
                                </ul>
                        </li>
                        {/if}
                    {/each}
                    </ul>
                </aside>
            </div>
            <div class="column">
                {#if params.library_category_id}
                    {#key params}
                        <LibraryControlEditor {assessment_type} category_id={params.library_category_id} subcategory_id={params.subcategory_id} control_id={params.control_id} />
                    {/key}
                {:else if params.control_id}
                    <ControlEditor {assessment_type} category_id={params.category_id} subcategory_id={params.subcategory_id} control_id={params.control_id}></ControlEditor>
                {:else}
                    <h1 class="title is-1">Select a control on the left</h1>
                {/if}
            </div>
        </div>
    </div>
</Loader>

{#if formData}
    <FormModal {...formData}></FormModal>
{/if}

<style>
    ul.menu-list label{
        display: inline-block;
        width: 100%;
        margin-top: 1em;

    }

    .button.is-small {
        margin-top: -5px;
    }

    .delete-control {
        width: 85px;
        overflow: hidden;
    }

    .button.is-small.hidden {
        width: 0;
        padding: 0;
        margin: 0;
        border: 0;
    }


    .column {
        overflow: auto;
        display: flex;
        flex-direction: column;
    }

    .field-body {
        flex-grow: 1;
    }

    .icon.edit {
        font-size: 15pt;
    }

</style>